import { call, put } from 'redux-saga/effects';
import { requestGetTask, requestGetTasks, requestGetTasksByUser, requestSetTaskAssignees, requestSetTaskResponsible, requestUpdateTask, requestCreateTask, requestUpdateTaskStatus, requestDeleteTaskAssignee, requestCompleteTask, requestDeleteTask, requestRestoreTask, requestToggleTaskBoard, requestUpdateTaskBlockers, requestGetBoardCards, requestAddBoardCard, requestUpdateBoardCard, requestMoveBoardCardColumn, requestRemoveBoardCard, requestUpdateBoardColumns, requestAttachBoardCardAttachments, requestDetachBoardCardAttachment, requestRetryBoardCardPush, requestAddChecklistItem, requestRenameChecklistItem, requestRemoveChecklistItem, requestToggleChecklistItem, requestImportTaskChecklist, requestSuggestedDueDate } from '../requests/task';
import { showAlert } from '../../slices/alerts';
import { triggerRefresh } from '../../slices/refreshTriggers';
import { safeErrorMessage } from '../../../utils/errorUtils';
import { isStaleColumnError, isMissingCardError } from '../../../utils/kanbanColumns';

const isGateConflict = (error) => error?.response?.status === 409 && error?.response?.data && (Array.isArray(error.response.data.blockers) || Array.isArray(error.response.data.incompleteItems));
const isTerminalStatus = (status) => status === 'Completed' || status === 'Cancelled';

export function* handleGetTask(action) {
    try {
        const { taskId, callback } = action.payload;
        const response = yield call(requestGetTask, taskId);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        const { callback } = action.payload || {};
        if (typeof callback === 'function') {
            callback(null, error);
        }
        yield put(showAlert({ severity: "error", message: safeErrorMessage(error) }));
    }
}

export function* handleGetTasks(action) {
    try {
        const { start, limit, callback } = action.payload;
        const response = yield call(requestGetTasks, start, limit);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleGetTasksByUser(action) {
    try {
        const { userId, callback } = action.payload;
        const response = yield call(requestGetTasksByUser, userId);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleSetUpdateTask(action) {
    try {
        const { taskId, taskData, callback } = action.payload;
        const response = yield call(requestUpdateTask, taskId, taskData);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleSetTaskResponsible(action) {
    try {
        const { taskId, responsible, callback } = action.payload;
        const response = yield call(requestSetTaskResponsible, taskId, responsible);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleSetTaskAssignees(action) {
    try {
        const { taskId, assignees, callback } = action.payload;
        const response = yield call(requestSetTaskAssignees, taskId, assignees);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleCreateTask(action) {
    try {
        const { taskData, callback } = action.payload;
        const response = yield call(requestCreateTask, taskData);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        const { callback } = action.payload || {};
        if (typeof callback === 'function') {
            callback(null);
        }
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleUpdateTaskStatus(action) {
    try {
        const { taskId, status, callback } = action.payload;
        const response = yield call(requestUpdateTaskStatus, taskId, status);
        if (isTerminalStatus(status)) {
            yield put(triggerRefresh({ triggerName: 'task' }));
        }
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        const { onGateBlocked } = action.payload || {};
        if (isGateConflict(error) && typeof onGateBlocked === 'function') {
            onGateBlocked(error.response.data);
            return;
        }
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleUpdateTaskStatusWithBypass(action) {
    try {
        const { taskId, status, callback } = action.payload;
        const response = yield call(requestUpdateTaskStatus, taskId, status, true);
        if (isTerminalStatus(status)) {
            yield put(triggerRefresh({ triggerName: 'task' }));
        }
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        const { onGateBlocked } = action.payload || {};
        if (isGateConflict(error) && typeof onGateBlocked === 'function') {
            onGateBlocked(error.response.data);
            return;
        }
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleDeleteTaskAssignee(action) {
    try {
        const { taskId, assignees, callback } = action.payload;
        const response = yield call(requestDeleteTaskAssignee, taskId, assignees);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleCompleteTask(action) {
    try {
        const { taskId, callback } = action.payload;
        const response = yield call(requestCompleteTask, taskId);
        yield put(triggerRefresh({ triggerName: 'task' }));
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        const { onGateBlocked } = action.payload || {};
        if (isGateConflict(error) && typeof onGateBlocked === 'function') {
            onGateBlocked(error.response.data);
            return;
        }
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleCompleteTaskWithBypass(action) {
    try {
        const { taskId, callback } = action.payload;
        const response = yield call(requestCompleteTask, taskId, true);
        yield put(triggerRefresh({ triggerName: 'task' }));
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        const { onGateBlocked } = action.payload || {};
        if (isGateConflict(error) && typeof onGateBlocked === 'function') {
            onGateBlocked(error.response.data);
            return;
        }
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleToggleTaskBoard(action) {
    try {
        const { taskId, hasBoard, callback } = action.payload;
        const response = yield call(requestToggleTaskBoard, taskId, hasBoard);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleGetBoardCards(action) {
    try {
        const { taskId, callback } = action.payload;
        const response = yield call(requestGetBoardCards, taskId);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        const { callback } = action.payload || {};
        if (typeof callback === 'function') {
            callback(null, error);
        }
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleAddBoardCard(action) {
    try {
        const { taskId, payload, callback } = action.payload;
        const response = yield call(requestAddBoardCard, taskId, payload);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        const { callback } = action.payload || {};
        if (typeof callback === 'function') {
            callback(null, error);
        }
        if (!isStaleColumnError(error)) {
            yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
        }
    }
}

export function* handleUpdateBoardCard(action) {
    try {
        const { taskId, cardId, fields, callback } = action.payload;
        const response = yield call(requestUpdateBoardCard, taskId, cardId, fields);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        const { callback } = action.payload || {};
        if (typeof callback === 'function') {
            callback(null, error);
        }
        if (!isMissingCardError(error)) {
            yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
        }
    }
}

export function* handleMoveBoardCardColumn(action) {
    try {
        const { taskId, cardId, columnId, callback } = action.payload;
        const response = yield call(requestMoveBoardCardColumn, taskId, cardId, columnId);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        const { callback } = action.payload || {};
        if (typeof callback === 'function') {
            callback(null, error);
        }
        if (!isStaleColumnError(error) && !isMissingCardError(error)) {
            yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
        }
    }
}

export function* handleRemoveBoardCard(action) {
    try {
        const { taskId, cardId, callback } = action.payload;
        const response = yield call(requestRemoveBoardCard, taskId, cardId);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        const { callback } = action.payload || {};
        if (typeof callback === 'function') {
            callback(null, error);
        }
        if (!isMissingCardError(error)) {
            yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
        }
    }
}

export function* handleUpdateBoardColumns(action) {
    try {
        const { taskId, columns, baseColumns, callback } = action.payload;
        const response = yield call(requestUpdateBoardColumns, taskId, columns, baseColumns);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        const { callback } = action.payload || {};
        if (typeof callback === 'function') {
            callback(null, error);
        }
        if (error?.response?.status !== 409) {
            yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
        }
    }
}

export function* handleAttachBoardCardAttachments(action) {
    try {
        const { taskId, cardId, payload, callback } = action.payload;
        const response = yield call(requestAttachBoardCardAttachments, taskId, cardId, payload);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        const { callback } = action.payload || {};
        if (typeof callback === 'function') {
            callback(null, error);
        }
        if (!isMissingCardError(error)) {
            yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
        }
    }
}

export function* handleDetachBoardCardAttachment(action) {
    try {
        const { taskId, cardId, attachmentId, callback } = action.payload;
        const response = yield call(requestDetachBoardCardAttachment, taskId, cardId, attachmentId);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        const { callback } = action.payload || {};
        if (typeof callback === 'function') {
            callback(null, error);
        }
        if (!isMissingCardError(error)) {
            yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
        }
    }
}

export function* handleRetryBoardCardPush(action) {
    try {
        const { taskId, cardId, payload, callback } = action.payload;
        const response = yield call(requestRetryBoardCardPush, taskId, cardId, payload);
        const { sent, resolved, message } = response.data || {};
        yield put(showAlert({ severity: resolved ? (sent ? 'success' : 'info') : 'warning', message: message || 'Sent to the board.' }));
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        const { callback } = action.payload || {};
        if (typeof callback === 'function') {
            callback(null, error);
        }
        // Running out of patience is not the same as failing: the push may still land, so the verdict is left to the pushState the server sends once it settles.
        const ranOut = !error?.response && (error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT');
        yield put(showAlert({
            severity: ranOut ? 'info' : 'error',
            message: ranOut
                ? 'BugHerd is taking longer than usual — this is still being sent, and the card updates itself once it lands.'
                : safeErrorMessage(error),
        }));
    }
}

export function* handleAddChecklistItem(action) {
    try {
        const { taskId, payload, callback } = action.payload;
        const response = yield call(requestAddChecklistItem, taskId, payload);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        const { callback } = action.payload || {};
        if (typeof callback === 'function') {
            callback(null, error);
        }
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleRenameChecklistItem(action) {
    try {
        const { taskId, itemId, name, callback } = action.payload;
        const response = yield call(requestRenameChecklistItem, taskId, itemId, name);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        const { callback } = action.payload || {};
        if (typeof callback === 'function') {
            callback(null, error);
        }
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleRemoveChecklistItem(action) {
    try {
        const { taskId, itemId, callback } = action.payload;
        const response = yield call(requestRemoveChecklistItem, taskId, itemId);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        const { callback } = action.payload || {};
        if (typeof callback === 'function') {
            callback(null, error);
        }
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleToggleChecklistItem(action) {
    try {
        const { taskId, itemId, completed, callback } = action.payload;
        const response = yield call(requestToggleChecklistItem, taskId, itemId, completed);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        const { callback } = action.payload || {};
        if (typeof callback === 'function') {
            callback(null, error);
        }
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleImportTaskChecklist(action) {
    try {
        const { taskId, checklistTemplateId, callback } = action.payload;
        const response = yield call(requestImportTaskChecklist, taskId, checklistTemplateId);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        const { callback } = action.payload || {};
        if (typeof callback === 'function') {
            callback(null, error);
        }
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleUpdateTaskBlockers(action) {
    try {
        const { taskId, add, remove, callback } = action.payload;
        const response = yield call(requestUpdateTaskBlockers, taskId, add, remove);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleDeleteTask(action) {
    try {
        const { taskId, callback } = action.payload;
        const response = yield call(requestDeleteTask, taskId);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleRestoreTask(action) {
    try {
        const { taskId, callback } = action.payload;
        const response = yield call(requestRestoreTask, taskId);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

// A failed suggestion is a convenience that did not land, not something to interrupt the form for:
// the caller keeps whatever due date is already in the field.
export function* handleGetSuggestedDueDate(action) {
    const { callback } = action.payload || {};
    try {
        const { project, responsible, hours } = action.payload;
        const response = yield call(requestSuggestedDueDate, project, responsible, hours);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        if (typeof callback === 'function') {
            callback(null, error);
        }
    }
}
